// Constants
export const HARD_MAX_DURATION_SECONDS = 10 * 60; // 10 minutes
export const HARD_MAX_FILE_SIZE_BYTES = 1024 * 1024 * 1024; // 1GB

export const ALLOWED_FORMATS: Record<string, string[]> = {
  "video/mp4": [".mp4"],
  "video/quicktime": [".mov"],
  "video/x-msvideo": [".avi"],
  "video/x-matroska": [".mkv"],
  "video/x-ms-wmv": [".wmv"],
  "video/webm": [".webm"],
};

export const ALLOWED_EXTENSIONS = [
  ".mp4",
  ".mov",
  ".avi",
  ".mkv",
  ".wmv",
  ".webm",
];

export const VIDEO_FORMATS = ["MP4", "MOV", "AVI", "MKV", "WMV", "WEBM"];

// Utility functions
export const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const formatBytes = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  if (mb < 1024) {
    return `${Math.round(mb)}MB`;
  }
  return `${(mb / 1024).toFixed(1)}GB`;
};

export const formatUploadSpeed = (bytesPerSecond?: number): string => {
  if (!bytesPerSecond || bytesPerSecond === 0) return "";
  const kbPerSecond = bytesPerSecond / 1024;
  if (kbPerSecond < 1024) {
    return `${Math.round(kbPerSecond)}KB/s`;
  }
  return `${(kbPerSecond / 1024).toFixed(1)}MB/s`;
};

export const getMediaDurationSeconds = (file: File): Promise<number> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const media = document.createElement("video");
    media.preload = "metadata";
    const cleanup = () => {
      URL.revokeObjectURL(url);
      media.remove();
    };
    media.onloadedmetadata = () => {
      const { duration } = media;
      cleanup();
      if (Number.isFinite(duration)) {
        resolve(Math.floor(duration));
      } else {
        reject(new Error("Unable to determine media duration"));
      }
    };
    media.onerror = () => {
      cleanup();
      reject(new Error("Unable to read media metadata"));
    };
    media.src = url;
  });

export type VideoMetadata = {
  width: number;
  height: number;
  bitRate: number;
};

export const getVideoMetadata = (
  file: File | Blob,
  durationSeconds: number
): Promise<VideoMetadata> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const media = document.createElement("video");
    media.preload = "metadata";
    const cleanup = () => {
      URL.revokeObjectURL(url);
      media.remove();
    };
    media.onloadedmetadata = () => {
      const width = media.videoWidth;
      const height = media.videoHeight;
      cleanup();

      if (width === 0 || height === 0) {
        reject(new Error("Unable to determine video dimensions"));
        return;
      }

      // Calculate bit rate: (file_size_bytes * 8 bits/byte) / duration_seconds
      const fileSizeBytes = file.size;
      const bitRate =
        durationSeconds > 0
          ? Math.floor((fileSizeBytes * 8) / durationSeconds)
          : 0;

      resolve({
        width,
        height,
        bitRate,
      });
    };
    media.onerror = () => {
      cleanup();
      reject(new Error("Unable to read video metadata"));
    };
    media.src = url;
  });

export const buildVideoConstraints = (
  deviceId?: string
): MediaTrackConstraints => {
  const constraints: MediaTrackConstraints = {
    facingMode: "user",
    width: { ideal: 1280 },
    height: { ideal: 720 },
  };

  if (deviceId) {
    constraints.deviceId = { exact: deviceId };
  }

  return constraints;
};

export const isVideoFormat = (format?: string): boolean => {
  if (!format) return false;
  return VIDEO_FORMATS.includes(format.toUpperCase());
};
